/**
 * Shared WCAG contrast math. Lives in `src/lib/color` alongside {@link ./hex} for the same reason
 * that module does: luminance and contrast-ratio arithmetic had started to be re-derived per
 * feature, and two implementations of a formula this fiddly drift silently.
 */

import { hexToRgb } from "./hex";

/** WCAG 2.1 AA minimum contrast ratio for normal-size text. */
export const WCAG_AA_NORMAL_TEXT_CONTRAST = 4.5;

/**
 * WCAG relative luminance for an sRGB color.
 *
 * @param rgb - 8-bit channels, 0–255.
 * @returns Relative luminance, 0 (black) to 1 (white).
 */
export function relativeLuminance(rgb: { r: number; g: number; b: number }): number {
  const channel = (c: number): number => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * channel(rgb.r) + 0.7152 * channel(rgb.g) + 0.0722 * channel(rgb.b);
}

/**
 * WCAG 2.1 contrast ratio between two hex colors.
 *
 * @param fgHex - Foreground color, `#rrggbb` with or without the hash.
 * @param bgHex - Background color, same format.
 * @returns The ratio, 1 (identical, or either input invalid) to 21 (black on white).
 */
export function contrastRatio(fgHex: string, bgHex: string): number {
  const fg = hexToRgb(fgHex);
  const bg = hexToRgb(bgHex);
  if (!fg || !bg) return 1;
  const L1 = relativeLuminance(fg);
  const L2 = relativeLuminance(bg);
  const [light, dark] = L1 > L2 ? [L1, L2] : [L2, L1];
  return (light + 0.05) / (dark + 0.05);
}

/**
 * Picks the better-contrasting of two text colors against a given background.
 *
 * Use when text sits over a color that isn't known at authoring time — a user-chosen card
 * background, or a data-series fill from a categorical palette that spans light and dark hues.
 *
 * @param bgHex - The background color the text sits over.
 * @param lightOption - Color to use on dark backgrounds (defaults to white).
 * @param darkOption - Color to use on light backgrounds, and the fallback for an invalid
 *   background (defaults to slate-900).
 * @returns Whichever of `lightOption`/`darkOption` has the higher WCAG contrast against `bgHex`.
 */
export function pickReadableOnBackground(
  bgHex: string,
  lightOption: string = "#ffffff",
  darkOption: string = "#0f172a"
): string {
  if (!hexToRgb(bgHex)) return darkOption;
  return contrastRatio(lightOption, bgHex) >= contrastRatio(darkOption, bgHex) ? lightOption : darkOption;
}
